import useMobileView from '@/hooks/useMobileView';
import { _s } from '@/locale';
import { Tracking } from '@/types/analytics';
import { createContext, ReactNode, useContext, useRef, useState } from 'react';
import { isMobile, isTablet } from 'react-device-detect';
import { DEFAULT_TAB_ITEMS, TabIdentifier } from './MegaMenu.constants';
import { TabIdentifier as Identifier, UseMegaMenuManagerOutput, UseMegaMenuManagerProps } from './MegaMenu.types';
import { trackMegaMenuClick } from './helpers';

const MOUSE_ENTER_DELAY_MS = 220;

const useMegaMenuManager = ({
  tabs = DEFAULT_TAB_ITEMS,
  navigationContainerId,
}: UseMegaMenuManagerProps): UseMegaMenuManagerOutput => {
  const navRef = useRef<HTMLElement>(null);
  const navigationContainerRef = useRef(null);
  const [activeTab, setActiveTab] = useState<Identifier | undefined>(undefined);
  const [activeSubcategory, setActiveSubcategory] = useState<Identifier | undefined>(undefined);
  const [blockTabHover, setBlockTabHover] = useState<Identifier | undefined>(undefined);
  const { isMobileView } = useMobileView();

  const timeoutId = useRef<NodeJS.Timeout | null>(null);

  function handleOnNavigationOpen(identifier: Identifier) {
    if (activeTab === identifier && isMobileView) {
      handleOnNavigatonLeave();
      return;
    }

    if (isMobile || isTablet) {
      document.body.style.overflow = 'hidden';
    }

    setActiveTab(identifier);
  }

  function handleOnSubCategoryNavigationOpen(
    identifier: Identifier,
    screenName: Tracking.ScreenName,
    track: boolean = true,
  ) {
    setActiveSubcategory(identifier);

    if (track) {
      trackMegaMenuClick(identifier, null, screenName);
    }
  }

  function handleOnSubCategoryNavigationLeave() {
    setActiveSubcategory(TabIdentifier.Categories);
    setActiveTab(TabIdentifier.Categories);
  }

  function handleOnNavigatonLeave(blockTabHover?: Identifier) {
    setActiveTab(undefined);
    setActiveSubcategory(undefined);
    document.body.style.overflow = 'auto';
    if (blockTabHover) setBlockTabHover(blockTabHover);
  }

  /**
   * Add some delay to the mouse enter event to avoid opening the submenu when the user is just hovering over the tab.
   */
  function handleOnMouseEnter(identifier: Identifier, routeMatch: any) {
    /**
     * if the user clicks a tab and navigates away from page we dont want the mouse over event to trigger opening
     * the menu again
     */
    if (blockTabHover === identifier) {
      setBlockTabHover(undefined);
      return;
    }

    if (isMobileView) return;

    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
    }

    if (activeTab) {
      handleOnSubCategoryNavigationOpen(identifier, routeMatch, false);
      setActiveTab(identifier);
      return;
    }

    timeoutId.current = setTimeout(() => {
      handleOnSubCategoryNavigationOpen(identifier, routeMatch, false);
      setActiveTab(identifier);
    }, MOUSE_ENTER_DELAY_MS);
  }

  function handleOnMouseLeave() {
    if (!timeoutId.current) return;
    clearTimeout(timeoutId.current);
  }

  return {
    navRef,
    navigationContainerRef,
    renderNavigationContainer: () => (
      <nav
        ref={navigationContainerRef}
        id={navigationContainerId}
        className="mega-menu-navigation-container"
        aria-label={_s(`features.mega-menu.navigation-container.aria-label`)}
      />
    ),
    navigationContainerId,
    tabs,
    activeTab,
    activeSubcategory,
    onNavigationOpen: handleOnNavigationOpen,
    onSubCategoryNavigationOpen: handleOnSubCategoryNavigationOpen,
    onSubCategoryNavigationLeave: handleOnSubCategoryNavigationLeave,
    onNavigationLeave: handleOnNavigatonLeave,
    onMouseEnter: handleOnMouseEnter,
    onMouseLeave: handleOnMouseLeave,
  };
};

export const MegaMenuContext = createContext<UseMegaMenuManagerOutput>({
  navigationContainerId: '',
  navRef: { current: null },
  navigationContainerRef: { current: null },
  tabs: DEFAULT_TAB_ITEMS,
  activeTab: undefined,
  activeSubcategory: undefined,
  onNavigationOpen: () => {},
  onSubCategoryNavigationOpen: () => {},
  onSubCategoryNavigationLeave: () => {},
  onNavigationLeave: () => {},
  onMouseEnter: () => {},
  onMouseLeave: () => {},
  renderNavigationContainer: () => null,
});

export const useMegaMenuContext = () => {
  const context = useContext(MegaMenuContext);

  if (!context) {
    throw new Error('useMegaMenuContext must be used within a UseMegaMenuProvider');
  }

  return context;
};

export const UseMegaMenuProvider: React.FC<{ children: ReactNode } & UseMegaMenuManagerProps> = ({
  children,
  ...props
}) => {
  return <MegaMenuContext.Provider value={useMegaMenuManager(props)}>{children}</MegaMenuContext.Provider>;
};
